import { useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Autocomplete,
  Button,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { debounce } from '@mui/material/utils';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useCatalogSearch } from '@/data/catalog/search';
import { useInstitutionsSearch } from '@/data/institutions';
import type { Course, Institution } from '@/models/catalog/course';
import { mountCourseOptions, type CourseOption } from '@/utils/course-options';

const schema = z.object({
  institution: z.object(
    { name: z.string(), id: z.number() },
    { message: 'Instituição de ensino obrigatória' }
  ),
  course: z.custom<CourseOption>((val) => val && typeof val === 'object', {
    message: 'Curso obrigatório',
  }),
});

type FormData = {
  institution: { id: number; name: string };
  course: CourseOption;
};

type InternalResourceDialogProps = {
  onClose: () => void;
  onSubmit: (data: Course) => void;
  defaultValues?: FormData;
};

const Actions = ({
  onSubmit,
  onClose,
}: {
  onSubmit: () => void;
  onClose: () => void;
}) => (
  <Stack spacing={2} direction="row" justifyContent="flex-end">
    <Button onClick={onClose}>Cancelar</Button>
    <Button variant="contained" onClick={onSubmit}>
      Salvar
    </Button>
  </Stack>
);

const DEBOUNCE_MS = 500;

export const InternalResourceDialog = ({
  onClose,
  onSubmit,
  defaultValues,
}: InternalResourceDialogProps) => {
  const { control, handleSubmit, watch, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const { institutions } = useInstitutionsSearch();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const selectedInstitution = watch('institution');

  const debounceCatalogSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchTerm(value);
      }, DEBOUNCE_MS),
    []
  );

  const filters = useMemo(() => {
    return {
      institutions: [selectedInstitution?.id],
      terms: debouncedSearchTerm,
    };
  }, [debouncedSearchTerm, selectedInstitution]);

  const { catalog, isFetching: catalogFetching } = useCatalogSearch({
    filters,
  });

  const courseOptions = mountCourseOptions(catalog || []);

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data.course.rawCourse);
  };

  return (
    <BaseDialog
      title="Adicionar recurso Galena"
      open
      fullWidth
      onClose={onClose}
      actionsComponent={
        <Actions onSubmit={handleSubmit(handleFormSubmit)} onClose={onClose} />
      }
    >
      <Stack spacing={2}>
        <Controller
          control={control}
          name="institution"
          render={({ field: { onChange }, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <FormLabel htmlFor="institution">Instituição</FormLabel>
              <Autocomplete
                id="institution"
                options={institutions || []}
                getOptionKey={(option: Institution) => option.id}
                getOptionLabel={(option: Institution) => option.name}
                onChange={(_, newValue) => {
                  onChange(newValue);
                  setValue('course', undefined as unknown as CourseOption);
                  setDebouncedSearchTerm('');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    placeholder="Selecione"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name="course"
          render={({ field: { onChange }, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <FormLabel htmlFor="course">Curso</FormLabel>
              <Autocomplete
                id="course"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={courseOptions}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => option.label}
                onInputChange={(_, value) => {
                  debounceCatalogSearch(value);
                }}
                onChange={(_, newValue) => onChange(newValue)}
                loading={catalogFetching}
                loadingText="Buscando cursos..."
                noOptionsText={
                  !debouncedSearchTerm
                    ? 'Digite o nome de um curso'
                    : 'Nenhum curso encontrado'
                }
                disabled={!selectedInstitution}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error}
                    helperText={error?.message}
                    placeholder="Selecione"
                    variant="outlined"
                  />
                )}
              />
            </FormControl>
          )}
        />
      </Stack>
    </BaseDialog>
  );
};
